import { useEffect } from 'react';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { onboardingApi } from '@/services';
import type { AutomationStage } from '@/types';
import { VALID_STAGES } from '@/constants/automation';
import { createBackoffPoller, type PollOutcome } from '@/utils/backoffPoller';
import { isAutomationInProgress } from '@/utils/automationStatus';
import { circuitPauseSignal } from '@/services/http/circuitBreaker';

const POLL_INTERVAL_MS = 3_000;
const MAX_BACKOFF_MS = 60_000;
// Consecutive network/5xx failures before we stop and surface a retry state
// instead of hammering a down backend forever (LIL-331). With backoff this
// spans ~4-5 minutes (3s → 6s → 12s → 24s → 48s → 60s …).
const MAX_CONSECUTIVE_FAILURES = 8;

/**
 * HTTP polling for automation status. Runs for the lifetime of the
 * automation, in parallel with the Socket.IO channel — socket events still
 * deliver updates first when they arrive, but polling guarantees the UI
 * stays in sync even when the socket is connected but not receiving events
 * (misrouted emits, missed room join, proxy buffering, etc.). Stops on
 * terminal status. Shared by AutomationProgressScreen (during onboarding)
 * and AutomationProgressTracker (after skip). LIL-219.
 *
 * Backed by a shared backoff poller (LIL-331): on consecutive failures it
 * slows down with jitter, pauses on hidden tabs, and after a hard cap stops
 * and surfaces a recoverable error instead of retrying silently forever.
 */
export function useAutomationPolling(enabled: boolean) {
  const updateAutomationStage = useOnboardingStore(state => state.updateAutomationStage);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const poll = async (): Promise<PollOutcome> => {
      const status = await onboardingApi.getAutomationStatus();
      if (cancelled) return 'done';

      // Adopt the backend's start time so the elapsed counter on the setup
      // surfaces measures the real run, not this browser session (LIL-440).
      useOnboardingStore.getState().setAutomationStartedAt(status.startedAt);

      for (const stage of status.stages) {
        if (!VALID_STAGES.includes(stage.name as AutomationStage)) continue;
        updateAutomationStage({
          stage: stage.name as AutomationStage,
          status: stage.status,
          progress: stage.progress,
          message: stage.message,
        });
      }

      // Stages the backend omits once automation has started are skipped
      // conditionally (e.g. indexing_knowledge_base when no KB files).
      // Flip them locally so they don't linger as 'pending'.
      if (status.status !== 'not_started') {
        const returned = new Set(status.stages.map(s => s.name));
        for (const stage of VALID_STAGES) {
          if (!returned.has(stage)) {
            updateAutomationStage({ stage, status: 'skipped' });
          }
        }
      }

      // Derive completion from the stages array, not just the top-level
      // status field. The backend sometimes keeps status pinned to a stage
      // name (e.g. 'finalizing_setup') after every stage has already
      // reported 'completed' — /onboarding's automationStatus flips to
      // 'completed' in that window but /automation/status lags, which is
      // why a refresh finalizes the user but live polling didn't.
      const errored = status.stages.find(
        s => VALID_STAGES.includes(s.name as AutomationStage) && s.status === 'error'
      );
      const effective = VALID_STAGES.map(name => {
        const match = status.stages.find(s => s.name === name);
        if (match) return match.status;
        return status.status !== 'not_started' ? 'skipped' : 'pending';
      });
      const allTerminal = effective.every(
        s => s === 'completed' || s === 'skipped' || s === 'error'
      );
      const hasCompleted = effective.some(s => s === 'completed');

      if (status.status === 'completed' || (allTerminal && hasCompleted && !errored)) {
        useOnboardingStore.getState().completeAutomation();
        return 'done';
      }
      if (status.status === 'failed' || errored) {
        useOnboardingStore.getState().setAutomationError({
          stage: (errored?.name as AutomationStage) ?? 'finalizing_setup',
          message: errored?.message || 'Automation failed on the server.',
          recoverable: true,
        });
        return 'done';
      }

      // The run is still live. Nothing else turns `isRunning` back on after a
      // reload wipes the non-persisted automation slice: useOnboardingStatusSync
      // does it only when its own request succeeds (its catch is a no-op), and
      // socket events never touch the flag. Without this the sidebar indicator
      // falls back to the amber "Complete Setup" link between stages while
      // setup is genuinely running (LIL-468).
      if (isAutomationInProgress(status.status)) {
        useOnboardingStore.getState().markAutomationRunning();
      }

      return 'continue';
    };

    const poller = createBackoffPoller(poll, {
      intervalMs: POLL_INTERVAL_MS,
      maxBackoffMs: MAX_BACKOFF_MS,
      maxConsecutiveFailures: MAX_CONSECUTIVE_FAILURES,
      pauseSignal: circuitPauseSignal,
      onGiveUp: () => {
        if (cancelled) return;
        // Backend has been unreachable across the whole backoff window. Stop
        // polling and let the user retry rather than keep hammering it.
        useOnboardingStore.getState().setAutomationError({
          stage: 'finalizing_setup',
          message: "We're having trouble reaching the server. Please retry.",
          recoverable: true,
        });
      },
    });

    return () => {
      cancelled = true;
      poller.stop();
    };
  }, [enabled, updateAutomationStage]);
}
